/**
 * Target-date helpers — pure and fully tested.
 *
 * Design (neuro-inclusive, deliberate): precision is always OPTIONAL.
 * - Quick chips (Today / Tomorrow / weekdays) are the one-tap core.
 * - The native calendar covers anything further out.
 * - Time is a third layer the user must reach for — never demanded.
 * - Free text ("after lunch", "end of week") remains first-class; vagueness
 *   is a feature for time-blind minds, not sloppiness.
 *
 * Chips and pickers also produce a machine-readable timestamp (deadlineAt)
 * behind the display text — the substrate for Phase D's date sort and,
 * later, opt-in reminders. Free text stores no timestamp: an honest absence
 * beats a guessed one.
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Tuesday 28 July" — today's anchor under the week header (year lives above). */
export function formatFullDate(d: Date = new Date()): string {
  return `${FULL_DAYS[d.getDay()]} ${d.getDate()} ${FULL_MONTHS[d.getMonth()]}`;
}

export interface QuickDate {
  /** Chip label ("Today", "Tomorrow", "Wed"). */
  label: string;
  /** Stored display text — always concrete ("Wed 30 Jul"), because a stored
   *  "Today" silently becomes a lie tomorrow. */
  hint: string;
  /** Machine timestamp (local noon — sortable, timezone-calm). */
  at: number;
}

export function formatDateHint(d: Date): string {
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** 12-hour clock, the register the AI already writes ("9:30 am"). */
export function formatTime(hours: number, minutes: number): string {
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h = hours % 12 === 0 ? 12 : hours % 12;
  return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

function atNoon(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0).getTime();
}

/** Today, Tomorrow, then the following four weekdays — one tap each. */
export function quickDateOptions(now: Date = new Date()): QuickDate[] {
  const opts: QuickDate[] = [];
  for (let i = 0; i < 6; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : DAYS[d.getDay()];
    opts.push({ label, hint: formatDateHint(d), at: atNoon(d) });
  }
  return opts;
}

/**
 * Convert native picker values (date "2026-07-30", optional time "09:30")
 * into display text + timestamp. Null on malformed input — the caller
 * falls back to free text rather than storing garbage.
 */
export function hintFromPicker(
  dateStr: string,
  timeStr?: string,
): { hint: string; at: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  let at = atNoon(d);
  let hint = formatDateHint(d);
  if (timeStr) {
    const t = /^(\d{1,2}):(\d{2})$/.exec(timeStr.trim());
    if (t) {
      const hours = Number(t[1]);
      const minutes = Number(t[2]);
      if (hours <= 23 && minutes <= 59) {
        at = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes).getTime();
        hint = `${hint}, ${formatTime(hours, minutes)}`;
      }
    }
  }
  return { hint, at };
}
